/**
 * launchStatusRows.js -- turn the server's readiness payload into the rows
 * of the Review & Launch status block (Trello #554).
 *
 * Two lists, one source each:
 *
 *   * ``mustFixRows``: the SERVER's launch reasons
 *     (state_transition_guard.compute_store_launch_eligibility). Nothing
 *     here decides whether a store can launch; it only words and links what
 *     the server already said. The headline count and the Launch button
 *     follow this list and nothing else.
 *   * ``recommendedRows``: guidance that does not stop a launch (section
 *     completeness, informational notes, a gate the platform waived, and the
 *     optional client).
 *
 * A row is ``{ key, text, fix }`` where ``fix`` is null or
 * ``{ type: "section" | "route" | "workspace", id?, route?, label }``.
 */
import { unreadySections } from "../unreadySections";
import { SECTION_META } from "../sectionRoutes";

export const LAUNCH_GATE_CODE_PREFIX = "launch_gate_";

// Reasons the Approval Workspace can resolve (artwork approval and
// digitization). A reason that names its own fix page is routed there
// instead; a reason with neither gets no link.
const WORKSPACE_CODES = new Set([
  "awaiting_approval",
  "changes_requested",
  "digitizing",
  "uploaded",
  "embroidery_not_digitized",
  "embroidery_digitization_unapproved",
]);

// The shipping blocker with no fix page of its own: the fix is to collect the
// shipping address at checkout (or add an address), and Checkout is where
// that is chosen.
const SHIPPING_UNRESOLVABLE_SECTION = "checkout";

// Where the section's required fields are known, name them. The client is
// NOT one of them (it is optional), so a store with no client must not read
// as if Client & Store Details were waiting on one.
const SECTION_MISSING_TEXT = {
  "client-store": "Client & Store Details needs a store name and a company name.",
};

const SECTION_TITLE = Object.fromEntries(SECTION_META.map((s) => [s.id, s.title]));

/**
 * The server writes "3 decoration(s) ..."; say "3 decorations" / "1
 * decoration has", so the count reads with its unit.
 */
export function withUnit(message, count) {
  const text = String(message || "");
  const n = Number(count);
  if (!Number.isFinite(n)) return text.replace(/\(s\)/g, "s");
  if (n === 1) {
    return text
      .replace(/\(s\) (have|need)\b/g, (_m, verb) => ` ${verb === "have" ? "has" : "needs"}`)
      .replace(/\(s\)/g, "");
  }
  return text.replace(/\(s\)/g, "s");
}

function sectionFix(id) {
  return { type: "section", id, label: `Fix in ${SECTION_TITLE[id] || id}` };
}

function routeFix(route) {
  return typeof route === "string" && route
    ? { type: "route", route, label: "Fix this" }
    : null;
}

const WORKSPACE_FIX = { type: "workspace", label: "Open Approval Workspace" };

function gateFor(gates, code) {
  const key = String(code).slice(LAUNCH_GATE_CODE_PREFIX.length);
  return (Array.isArray(gates) ? gates : []).find((g) => g.key === key) || null;
}

/** Rows for a list of server reasons (``launch.reasons`` or ``blockers``). */
export function reasonRows(reasons, gates) {
  const rows = [];
  for (const r of Array.isArray(reasons) ? reasons : []) {
    const code = String(r.code || "");

    if (code === "sections_incomplete") {
      const sections = unreadySections(r.tabs);
      if (sections.length) {
        for (const sec of sections) {
          rows.push({
            key: `sections_incomplete-${sec.id}`,
            text: SECTION_MISSING_TEXT[sec.id] || `${sec.title} still needs information.`,
            fix: sectionFix(sec.id),
          });
        }
        continue;
      }
    }

    if (code.startsWith(LAUNCH_GATE_CODE_PREFIX)) {
      const gate = gateFor(gates, code);
      rows.push({
        key: code,
        // The gate's own reason names only what is still missing; the
        // reason's message is the generic fallback.
        text: gate?.reason || r.message || "",
        fix: routeFix(r.next_step_route || gate?.next_step_route),
      });
      continue;
    }

    let fix = null;
    if (r.fix_route) fix = routeFix(r.fix_route);
    else if (WORKSPACE_CODES.has(code)) fix = WORKSPACE_FIX;
    else if (code === "shipping_unresolvable") fix = sectionFix(SHIPPING_UNRESOLVABLE_SECTION);
    rows.push({ key: code, text: withUnit(r.message, r.count), fix });
  }
  return rows;
}

// What a store without a client loses (api: store_approval_recipients fails
// closed, portal reads filter on client_id, per_color_approval, and the
// portal config refuses a store with no client).
export const NO_CLIENT_TEXT =
  "No client is linked to this store. Without a client these are off: " +
  "approval emails, the client portal and reports, per-client approvals " +
  "and department mapping.";

/**
 * Recommended rows: everything that does not stop a launch.
 *
 * ``mustFixSectionIds`` are sections already listed under Must fix, so a
 * section is never named twice.
 */
export function recommendedRows({
  warnSectionIds,
  mustFixSectionIds,
  hasClient,
  info,
  gates,
  mustFixGateCodes,
  answered,
}) {
  const rows = [];

  if (!hasClient) {
    rows.push({
      key: "no-client",
      text: NO_CLIENT_TEXT,
      fix: sectionFix("client-store"),
      emphasis: true,
    });
  }

  for (const id of warnSectionIds) {
    if (mustFixSectionIds.has(id)) continue;
    // The specific no-client line above replaces the generic one.
    if (id === "client-store" && !hasClient) continue;
    rows.push({
      key: `section-${id}`,
      text: `${SECTION_TITLE[id] || id} needs attention.`,
      fix: sectionFix(id),
    });
  }

  // An account check the platform is not enforcing (SA switch, or a gate
  // waived for this distributor) is still worth finishing. Judged only once
  // the server has answered which checks DO block.
  if (answered) {
    for (const g of Array.isArray(gates) ? gates : []) {
      if (g.met) continue;
      const code = `${LAUNCH_GATE_CODE_PREFIX}${g.key}`;
      if (mustFixGateCodes.has(code)) continue;
      rows.push({
        key: code,
        text: `${g.reason || g.next_step} This is not blocking launch.`,
        fix: routeFix(g.next_step_route),
      });
    }
  }

  for (const note of Array.isArray(info) ? info : []) {
    rows.push({ key: `info-${note.code}`, text: note.message, fix: null });
  }
  return rows;
}
